import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Calculator, CheckCircle, MapPin, Loader,
  Camera, Image as ImageIcon, X, DollarSign, Phone
} from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { resizeImage } from '../utils/imageUtils';

const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500 focus:ring-1 focus:ring-apex-blue-500";

// ── Service types ──────────────────────────────────────────────────────────
const SERVICE_TYPES = [
  { id:'Standard Towing',           label:'Standard Towing',         icon:'🚗', type:'towing' },
  { id:'Emergency Towing',          label:'Emergency Towing',        icon:'🚨', type:'towing' },
  { id:'Long Distance Transport',   label:'Long Distance',           icon:'🗺️', type:'towing' },
  { id:'Heavy Machinery Transport', label:'Heavy Transport',         icon:'🏗️', type:'towing' },
  { id:'Cash for Cars',             label:'Cash for Cars',           icon:'💵', type:'cash'   },
  { id:'Free Car Removal',          label:'Free Car Removal',        icon:'🗑️', type:'free'   },
];

const VEHICLE_TYPES = [
  { id:'car',   label:'Car / Sedan', icon:'🚗', mult:1.0  },
  { id:'suv',   label:'SUV / 4WD',  icon:'🚙', mult:1.15 },
  { id:'ute',   label:'Ute / Van',  icon:'🛻', mult:1.2  },
  { id:'truck', label:'Truck',       icon:'🚛', mult:1.35 },
  { id:'moto',  label:'Motorcycle', icon:'🏍️', mult:0.8  },
  { id:'heavy', label:'Heavy Mach.',icon:'🏗️', mult:2.5  },
];

const TOW_CONDITIONS = [
  { id:'running',          label:'Running',         fee:0   },
  { id:'not_running',      label:'Not Running',     fee:50  },
  { id:'accident_damaged', label:'Accident Damaged',fee:100 },
];

const CAR_CONDITIONS = ['Excellent','Good','Fair','Poor','Scrap'];
const DEFAULT_CAR_VALUES = {
  Excellent:{ min:1500, max:3500 },
  Good:     { min:700,  max:1800 },
  Fair:     { min:300,  max:800  },
  Poor:     { min:100,  max:400  },
  Scrap:    { min:50,   max:250  },
};

const EXPECTATIONS = [
  { id:'under_200', label:'Under $200',      range:[0,200],        hint:'Scrap / parts only'  },
  { id:'200_500',   label:'$200 – $500',     range:[200,500],      hint:'Poor / not running'  },
  { id:'500_1000',  label:'$500 – $1,000',   range:[500,1000],     hint:'Fair condition'      },
  { id:'1000_2000', label:'$1,000 – $2,000', range:[1000,2000],    hint:'Good condition'      },
  { id:'2000_plus', label:'$2,000+',         range:[2000,99999],   hint:'Excellent / low km'  },
  { id:'not_sure',  label:'Not sure',        range:[0,99999],      hint:'Let us assess it'    },
];

// ── Calculations ──────────────────────────────────────────────────────────
function calcCash(condition, yearStr, vehicleType, p) {
  // Base offer from settings (or defaults)
  const defaults = { Excellent:{min:1500,max:3500}, Good:{min:700,max:1800}, Fair:{min:300,max:800}, Poor:{min:100,max:400}, Scrap:{min:50,max:250} };
  const base = p ? {
    min: Number(p[`cash_${condition.toLowerCase()}_min`]) || (defaults[condition]?.min || 100),
    max: Number(p[`cash_${condition.toLowerCase()}_max`]) || (defaults[condition]?.max || 500),
  } : (defaults[condition] || { min:100, max:500 });

  // Age depreciation from settings
  const deprPct  = Number(p?.age_depreciation_pct) || 3.5;
  const floorPct = Number(p?.age_floor_pct) || 40;
  const age      = new Date().getFullYear() - (parseInt(yearStr) || 2010);
  const aMult    = Math.max(floorPct / 100, 1 - age * (deprPct / 100));

  // Vehicle type multiplier from settings
  const vMultMap = {
    car:   Number(p?.mult_car)   || 1.0,
    suv:   Number(p?.mult_suv)   || 1.15,
    ute:   Number(p?.mult_ute)   || 1.2,
    truck: Number(p?.mult_truck) || 1.35,
    moto:  Number(p?.mult_moto)  || 0.8,
    heavy: Number(p?.mult_heavy) || 2.5,
  };
  const vMult = vMultMap[vehicleType] || 1.0;

  return {
    min: Math.max(50, Math.round(base.min * aMult * vMult / 50) * 50),
    max: Math.max(50, Math.round(base.max * aMult * vMult / 50) * 50),
  };
}

function calcTow(serviceId, vehicleType, km, condition) {
  const BASE = { 'Standard Towing':80,'Emergency Towing':130,'Long Distance Transport':120,'Heavy Machinery Transport':200 };
  const base  = BASE[serviceId] || 80;
  const dist  = Number(km) || 10;
  const perKm = dist > 50 ? 4.5 : 3.5;
  const vMult = VEHICLE_TYPES.find(v => v.id === vehicleType)?.mult || 1;
  const fee   = TOW_CONDITIONS.find(c => c.id === condition)?.fee || 0;
  const raw   = (base + dist * perKm + fee) * vMult;
  return { min: Math.round(raw * 0.9 / 5) * 5, max: Math.round(raw * 1.1 / 5) * 5 };
}

async function geocode(suburb) {
  if (!suburb?.trim() || suburb.trim().length < 2) return null;
  const q = encodeURIComponent(`${suburb.trim()}, Brisbane, QLD, Australia`);
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=au`,
      { headers: { 'Accept-Language':'en', 'User-Agent':'ApexTowing/1.0' } }
    );
    const d = await r.json();
    return d[0] ? { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon) } : null;
  } catch { return null; }
}

function haversineKm(a, b) {
  const R = 6371, dLat = (b.lat-a.lat)*Math.PI/180, dLng = (b.lng-a.lng)*Math.PI/180;
  const x = Math.sin(dLat/2)**2 + Math.cos(a.lat*Math.PI/180)*Math.cos(b.lat*Math.PI/180)*Math.sin(dLng/2)**2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1-x)) * 1.38);
}

// ── Tow Truck Animation ────────────────────────────────────────────────────
function TowTruckAnimation({ message = 'Calculating your route...' }) {
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-apex-blue-100 mb-4">
      <style>{`
        @keyframes apexTruck {
          0%   { left: -120px; }
          100% { left: 110%;   }
        }
        .apex-truck {
          position: absolute;
          bottom: 8px;
          left: -120px;
          animation: apexTruck 2.4s linear infinite;
          font-size: 36px;
          white-space: nowrap;
          line-height: 1;
          filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.3));
        }
      `}</style>
      {/* Scene */}
      <div style={{ position:'relative', height:110, overflow:'hidden', background:'linear-gradient(180deg, #bfdbfe 0%, #dbeafe 55%, #374151 55%, #1f2937 100%)' }}>
        {/* Clouds */}
        <span style={{ position:'absolute', left:'8%',  top:8,  fontSize:22 }}>☁️</span>
        <span style={{ position:'absolute', left:'48%', top:5,  fontSize:28 }}>☁️</span>
        <span style={{ position:'absolute', right:'12%',top:12, fontSize:18 }}>☁️</span>
        {/* Lane markings */}
        <div style={{ position:'absolute', bottom:16, left:0, right:0, display:'flex', gap:12, padding:'0 8px', overflow:'hidden' }}>
          {Array(14).fill(0).map((_, i) => (
            <div key={i} style={{ width:28, height:4, background:'#F5C400', borderRadius:2, flexShrink:0 }} />
          ))}
        </div>
        {/* Truck */}
        <div className="apex-truck">🚛🚗</div>
      </div>
      {/* Label */}
      <div className="bg-white border-t-2 border-apex-blue-100 text-center py-3">
        <p className="text-apex-blue-700 font-black text-sm">{message}</p>
        <p className="text-gray-400 text-xs mt-0.5">Usually takes a couple of seconds</p>
      </div>
    </div>
  );
}

// ── Cash for Cars result screen ────────────────────────────────────────────
function CashResult({ estimate, expectation, form, photos, onBookNow, onCallUs }) {
  const { settings, telHref } = useSettings();
  const exp    = EXPECTATIONS.find(e => e.id === expectation) || EXPECTATIONS[5];
  const expMin = exp.range[0];
  const expMax = exp.range[1];

  // Determine outcome
  let outcome;
  if (expectation === 'not_sure') {
    outcome = 'success';
  } else if (estimate.min > expMax && expMax < 99999) {
    outcome = 'bonus'; // we pay MORE than they expected
  } else if (estimate.max >= expMin) {
    outcome = 'success'; // we can meet their expectation
  } else {
    outcome = 'partial'; // we can't quite meet it
  }

  return (
    <div className="space-y-4">
      {/* Result card */}
      {outcome === 'bonus' && (
        <div className="rounded-2xl p-6 text-white text-center" style={{ background:'linear-gradient(135deg, #059669, #047857)' }}>
          <div className="text-5xl mb-3">💰</div>
          <p className="text-white/80 text-sm font-bold uppercase tracking-wide mb-1">Great News!</p>
          <p className="text-2xl font-black mb-2">We Can Pay More Than You Expected!</p>
          <p className="text-white/70 text-sm mb-4">You expected {exp.label} — we can offer:</p>
          <div className="bg-white/20 rounded-xl p-4 mb-4">
            <p className="text-5xl font-black">${estimate.min.toLocaleString()}–${estimate.max.toLocaleString()}</p>
            <p className="text-white/60 text-xs mt-1">Cash in hand · Same day · Final after inspection</p>
          </div>
          <div className="flex gap-3">
            <button onClick={onBookNow} className="flex-1 bg-white text-green-700 font-black py-3 rounded-xl hover:bg-green-50 flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" /> Book Pickup Now
            </button>
          </div>
        </div>
      )}

      {outcome === 'success' && (
        <div className="rounded-2xl p-6 text-white text-center" style={{ background:'linear-gradient(135deg, #16a34a, #15803d)' }}>
          <div className="text-5xl mb-3">🎉</div>
          <p className="text-white/80 text-sm font-bold uppercase tracking-wide mb-1">Congrats!</p>
          <p className="text-2xl font-black mb-2">Yes — We Can Meet Your Expectation!</p>
          {expectation !== 'not_sure' && <p className="text-white/70 text-sm mb-3">You expected {exp.label} — we can offer:</p>}
          <div className="bg-white/20 rounded-xl p-4 mb-4">
            <p className="text-5xl font-black">${estimate.min.toLocaleString()}–${estimate.max.toLocaleString()}</p>
            <p className="text-white/60 text-xs mt-1">Cash in hand · Same day · Final after inspection</p>
          </div>
          <div className="flex gap-3">
            <button onClick={onBookNow} className="flex-1 bg-white text-green-700 font-black py-3 rounded-xl hover:bg-green-50 flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" /> Book Pickup Now
            </button>
            <a href={telHref()} className="flex-1 bg-white/20 text-white font-bold py-3 rounded-xl hover:bg-white/30 flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" /> Call Us
            </a>
          </div>
        </div>
      )}

      {outcome === 'partial' && (
        <div className="rounded-2xl p-6 text-white text-center" style={{ background:'linear-gradient(135deg, #d97706, #b45309)' }}>
          <div className="text-5xl mb-3">🤝</div>
          <p className="text-white/80 text-sm font-bold uppercase tracking-wide mb-1">Almost There</p>
          <p className="text-2xl font-black mb-2">We Can Offer Up To:</p>
          <div className="bg-white/20 rounded-xl p-4 mb-2">
            <p className="text-5xl font-black">${estimate.max.toLocaleString()}</p>
            <p className="text-white/60 text-xs mt-1">Cash in hand · Same day · Final after inspection</p>
          </div>
          <p className="text-white/70 text-sm mb-4">Your expectation was <strong>{exp.label}</strong> — the final offer may be higher after we inspect. Call us for a better quote!</p>
          <div className="flex gap-3">
            <a href={telHref()} className="flex-1 bg-white text-amber-700 font-black py-3 rounded-xl hover:bg-amber-50 flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" /> Call for Better Quote
            </a>
            <button onClick={onBookNow} className="flex-1 bg-white/20 text-white font-bold py-3 rounded-xl hover:bg-white/30 flex items-center justify-center gap-2">
              Book at This Price
            </button>
          </div>
        </div>
      )}

      {/* Details recap */}
      <div className="bg-gray-50 rounded-xl p-4 text-sm">
        <p className="text-xs font-bold text-gray-500 uppercase mb-2">Your Vehicle Details</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            ['Vehicle', `${form.vehicle_year || ''} ${form.vehicle_make || ''} ${form.vehicle_model || ''}`],
            ['Condition', form.cash_condition],
            ['Type', VEHICLE_TYPES.find(v=>v.id===form.vehicle_type)?.label || '—'],
            ['Location', form.pickup || '—'],
          ].map(([l,v]) => v.trim() ? (
            <div key={l}><span className="text-gray-400">{l}: </span><span className="font-semibold text-gray-700">{v}</span></div>
          ) : null)}
        </div>
      </div>
    </div>
  );
}

// ── Photo Uploader ────────────────────────────────────────────────────────
function PhotoUploader({ photos, onChange, label = 'Add Photos (optional)' }) {
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newPhotos = await Promise.all(files.map(f => resizeImage(f)));
      onChange([...photos, ...newPhotos]);
    } catch { alert('Photo upload failed.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div>
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{label}</p>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {photos.map((src, i) => (
            <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button onClick={() => onChange(photos.filter((_,idx)=>idx!==i))}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*"                       multiple className="hidden" onChange={handleFile} />
      {uploading ? (
        <div className="flex items-center gap-2 text-sm text-apex-blue-600 py-2"><Loader className="w-4 h-4 animate-spin" /> Uploading...</div>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => cameraRef.current?.click()}
            className="flex-1 py-2.5 border-2 border-dashed border-apex-blue-200 rounded-xl text-xs font-bold text-apex-blue-600 hover:bg-apex-blue-50 flex items-center justify-center gap-1.5">
            <Camera className="w-4 h-4" /> Take Photo
          </button>
          <button type="button" onClick={() => galleryRef.current?.click()}
            className="flex-1 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-apex-blue-200 hover:text-apex-blue-500 flex items-center justify-center gap-1.5">
            <ImageIcon className="w-4 h-4" /> Choose Files
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function EstimatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings, telHref } = useSettings();
  const [pricingSettings, setPricingSettings] = useState(null);

  useEffect(() => {
    api.get('/api/settings/pricing').then(r => setPricingSettings(r.data)).catch(() => {});
  }, []);

  // Pre-select service from URL: /estimate?service=Emergency+Towing
  const urlService = searchParams.get('service');
  const initialService = SERVICE_TYPES.find(s => s.id === urlService)?.id || 'Standard Towing';
  const [serviceType, setServiceType] = useState(initialService);
  const [form, setForm] = useState({
    customer_name:'', customer_phone:'', customer_email:'',
    pickup:'', dropoff:'',
    vehicle_type:'car', condition:'running',
    vehicle_make:'', vehicle_model:'', vehicle_year:'', vehicle_rego:'',
    cash_condition:'', expectation:'not_sure',
  });
  const [photos, setPhotos]               = useState([]);
  const [distKm, setDistKm]               = useState('');
  const [distLoading, setDistLoading]     = useState(false);
  const [distError, setDistError]         = useState('');
  const [estimateReady, setEstimateReady] = useState(false); // true after animation done
  const [showCashResult, setShowCashResult]     = useState(false);
  const [cashEstLoading, setCashEstLoading]     = useState(false);
  const [submitted, setSubmitted]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const debounceRef = useRef(null);

  const svc = SERVICE_TYPES.find(s => s.id === serviceType) || SERVICE_TYPES[0];
  const set = k => e => setForm(f => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  // Reset estimate when service or key fields change
  useEffect(() => { setDistKm(''); setEstimateReady(false); setDistError(''); setShowCashResult(false); setCashEstLoading(false); }, [serviceType]);

  // ── Auto-calculate distance when BOTH suburbs filled (towing only) ──────
  useEffect(() => {
    if (svc.type !== 'towing') return;
    setEstimateReady(false);
    setDistKm('');
    setDistError('');

    if (!form.pickup.trim() || !form.dropoff.trim()) return;
    if (form.pickup.trim().length < 2 || form.dropoff.trim().length < 2) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setDistLoading(true);
      const startMs = Date.now();
      try {
        const a = await geocode(form.pickup);
        await new Promise(r => setTimeout(r, 350)); // Nominatim rate limit
        const b = await geocode(form.dropoff);
        if (a && b) {
          const km = haversineKm(a, b);
          // Ensure animation shows for at least 2 seconds
          const elapsed = Date.now() - startMs;
          if (elapsed < 2000) await new Promise(r => setTimeout(r, 2000 - elapsed));
          setDistKm(String(km));
          setDistError('');
          setEstimateReady(true);
        } else {
          await new Promise(r => setTimeout(r, Math.max(0, 2000 - (Date.now() - startMs))));
          setDistError('Could not find one of those suburbs — try a postcode or nearby landmark.');
        }
      } catch {
        setDistError('Distance lookup failed. Enter km manually below.');
      } finally {
        setDistLoading(false);
      }
    }, 900);

    return () => clearTimeout(debounceRef.current);
  }, [form.pickup, form.dropoff, svc.type]);

  const towEst  = svc.type === 'towing' && estimateReady && distKm ? calcTow(serviceType, form.vehicle_type, distKm, form.condition) : null;
  // For cash: only calculate once condition is explicitly selected
  const cashEst = svc.type === 'cash' && form.cash_condition
    ? calcCash(form.cash_condition, form.vehicle_year || '2012', form.vehicle_type, pricingSettings)
    : null;

  const canSubmit = form.customer_name && form.customer_phone && form.pickup;

  const [savedEstimateId, setSavedEstimateId] = useState(null);

  // ── Save to sessionStorage and navigate to BookingPage ─────────────────
  const goToBook = useCallback((estId) => {
    // Guard: estId may be a MouseEvent when used directly as onClick — ignore it
    const resolvedEstId = (typeof estId === 'string' ? estId : null) || savedEstimateId || null;
    const prefill = {
      from:              'estimate',
      estimate_id:       resolvedEstId,  // BookingPage fetches photos from this
      service_type:      serviceType,
      customer_name:     form.customer_name,
      customer_phone:    form.customer_phone,
      customer_email:    form.customer_email,
      pickup_address:    form.pickup,
      pickup_suburb:     form.pickup,
      dropoff_address:   form.dropoff,
      dropoff_suburb:    form.dropoff,
      vehicle_make:      form.vehicle_make,
      vehicle_model:     form.vehicle_model,
      vehicle_year:      form.vehicle_year,
      vehicle_rego:      form.vehicle_rego,
      vehicle_condition: svc.type === 'cash' ? form.cash_condition : form.condition,
      est_min:           towEst?.min || cashEst?.min || 0,
      est_max:           towEst?.max || cashEst?.max || 0,
      // NOTE: photos NOT stored here — too large for sessionStorage (5MB limit)
      // BookingPage fetches them from the estimate via estimate_id
    };
    try {
      sessionStorage.setItem('apex_booking_prefill', JSON.stringify(prefill));
    } catch(e) {
      // If even this fails, store minimal version
      try {
        sessionStorage.setItem('apex_booking_prefill', JSON.stringify({
          from: 'estimate', estimate_id: resolvedEstId, service_type: serviceType,
          customer_name: form.customer_name, customer_phone: form.customer_phone,
        }));
      } catch {}
    }
    navigate('/book');
  }, [serviceType, form, towEst, cashEst, navigate, svc.type, savedEstimateId]);

  const submit = async () => {
    if (svc.type === 'cash') {
      setCashEstLoading(true);
      const startMs = Date.now();
      // Save estimate and capture its ID
      let estId = null;
      try {
        const res = await api.post('/api/estimates', {
          customer_name: form.customer_name, customer_phone: form.customer_phone,
          customer_email: form.customer_email, pickup: form.pickup, dropoff: '—',
          vehicle_type: form.vehicle_type, service_type: serviceType,
          condition: form.cash_condition, cash_condition: form.cash_condition,
          expectation: EXPECTATIONS.find(e=>e.id===form.expectation)?.label || '',
          vehicle_make: form.vehicle_make, vehicle_model: form.vehicle_model,
          vehicle_year: form.vehicle_year, vehicle_rego: form.vehicle_rego,
          distance_km: 0,
          est_min: cashEst?.min || 0, est_max: cashEst?.max || 0,
          images: photos,
          notes: [
            form.vehicle_year  && `Year: ${form.vehicle_year}`,
            form.vehicle_make  && `Make: ${form.vehicle_make}`,
            form.vehicle_model && `Model: ${form.vehicle_model}`,
            form.vehicle_rego  && `Rego: ${form.vehicle_rego}`,
            `Expectation: ${EXPECTATIONS.find(e=>e.id===form.expectation)?.label}`,
            photos.length && `${photos.length} photos attached`,
          ].filter(Boolean).join(' | '),
        });
        estId = res.data?.id;
        setSavedEstimateId(estId);
      } catch {}
      const elapsed = Date.now() - startMs;
      await new Promise(r => setTimeout(r, Math.max(2000, elapsed)));
      setCashEstLoading(false);
      setShowCashResult(true);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/estimates', {
        customer_name: form.customer_name, customer_phone: form.customer_phone,
        customer_email: form.customer_email, pickup: form.pickup, dropoff: form.dropoff,
        vehicle_type: form.vehicle_type, service_type: serviceType,
        condition: form.condition, distance_km: Number(distKm) || 0,
        est_min: towEst?.min || 0, est_max: towEst?.max || 0,
        images: photos,
        notes: photos.length ? `${photos.length} photos attached` : '',
      });
      setSavedEstimateId(res.data?.id);
      setSubmitted(true);
    } catch { alert(`Could not submit — please call ${settings.company_phone}`); }
    finally { setLoading(false); }
  };

  // ── Success screen (towing/free) ─────────────────────────────────────────
  if (submitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center max-w-md w-full shadow-xl">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-black text-apex-blue-700 mb-2">
          {svc.type === 'free' ? 'Request Received!' : 'Estimate Submitted!'}
        </h2>
        <p className="text-gray-500 mb-5">We'll call <strong>{form.customer_phone}</strong> shortly.</p>
        {towEst && (
          <div className="bg-apex-blue-700 rounded-2xl p-5 mb-5">
            <p className="text-white/60 text-xs font-bold uppercase mb-1">Estimated Range</p>
            <p className="text-4xl font-black text-apex-yellow-400">${towEst.min.toLocaleString()} – ${towEst.max.toLocaleString()}</p>
            <p className="text-white/40 text-xs mt-1">incl. GST · ~{distKm} km · final price on arrival</p>
          </div>
        )}
        {svc.type === 'free' && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 mb-5">
            <p className="text-3xl font-black text-green-600">FREE!</p>
            <p className="text-sm text-green-700 mt-1">No charges of any kind</p>
          </div>
        )}
        <div className="flex gap-3 justify-center">
          <button onClick={() => goToBook()} className="bg-apex-yellow-500 text-apex-blue-700 font-black px-6 py-3 rounded-xl hover:bg-apex-yellow-400">Book Now</button>
          <Link to="/" className="border-2 border-gray-200 text-gray-600 font-bold px-6 py-3 rounded-xl hover:bg-gray-50">Home</Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-apex-blue-700 text-white px-4 py-5">
        <div className="max-w-xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-white/70 text-sm mb-4 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-apex-yellow-500 rounded-xl flex items-center justify-center">
              <Calculator className="w-5 h-5 text-apex-blue-700" />
            </div>
            <div>
              <h1 className="font-black text-xl">Get an Estimate</h1>
              <p className="text-white/60 text-xs">Instant price guide · All Brisbane suburbs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">

          {/* Service picker */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">What do you need?</p>
            <div className="grid grid-cols-2 gap-2">
              {SERVICE_TYPES.map(s => (
                <button key={s.id} onClick={() => { setServiceType(s.id); setShowCashResult(false); }}
                  className={`p-3 rounded-xl border-2 text-sm font-semibold text-left flex items-center gap-2 transition-all
                    ${serviceType===s.id ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  <span>{s.icon}</span>{s.label}
                </button>
              ))}
            </div>
          </div>

          {/* ─── TOWING FLOW ─────────────────────────────────────────── */}
          {svc.type === 'towing' && !showCashResult && (<>
            {/* Vehicle type */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Vehicle Type</p>
              <div className="grid grid-cols-3 gap-2">
                {VEHICLE_TYPES.map(v => (
                  <button key={v.id} onClick={() => set('vehicle_type')(v.id)}
                    className={`p-3 rounded-xl border-2 text-xs font-semibold text-center transition-all ${form.vehicle_type===v.id ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    <div className="text-xl mb-1">{v.icon}</div>{v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Condition */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Vehicle Condition</p>
              <div className="grid grid-cols-3 gap-2">
                {TOW_CONDITIONS.map(c => (
                  <button key={c.id} onClick={() => set('condition')(c.id)}
                    className={`p-3 rounded-xl border-2 text-xs font-semibold text-center transition-all ${form.condition===c.id ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 text-gray-700'}`}>
                    {c.label}
                    {c.fee > 0 && <span className="block text-[9px] text-gray-400 mt-0.5">+${c.fee}</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Suburbs — BOTH required before calc */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                Pickup & Drop-off — <span className="text-gray-400 font-normal normal-case">Enter both to get your estimate</span>
              </p>
              <div className="space-y-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                  <input value={form.pickup} onChange={set('pickup')} className={`${inputCls} pl-9`}
                    placeholder="Pickup suburb or postcode (e.g. Chermside 4032)" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                  <input value={form.dropoff} onChange={set('dropoff')} className={`${inputCls} pl-9`}
                    placeholder="Drop-off suburb or postcode (e.g. Brisbane CBD 4000)" />
                </div>
              </div>

              {/* Status */}
              {!form.pickup.trim() || !form.dropoff.trim() ? (
                <p className="text-xs text-gray-400 mt-2 text-center">↑ Enter both locations to calculate your estimate</p>
              ) : null}
              {distError && !distLoading && (
                <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">{distError}</div>
              )}
            </div>

            {/* Loading animation */}
            {distLoading && (
              <TowTruckAnimation message={`Finding route: ${form.pickup} → ${form.dropoff}`} />
            )}

            {/* Manual km input if geocoding failed */}
            {distError && !distLoading && (
              <label className="block">
                <span className="text-xs font-bold text-gray-400 uppercase">Enter Distance Manually (km)</span>
                <input type="number" value={distKm} onChange={e => { setDistKm(e.target.value); setEstimateReady(!!e.target.value); }}
                  className={`${inputCls} mt-1`} placeholder="e.g. 15" />
              </label>
            )}

            {/* Estimate (only shown after calc) */}
            {estimateReady && towEst && (
              <div className="bg-apex-blue-700 rounded-2xl p-5 text-center">
                <p className="text-white/60 text-xs font-bold uppercase tracking-wide mb-1">Estimated Cost</p>
                <p className="text-apex-yellow-400 text-4xl font-black">${towEst.min.toLocaleString()} – ${towEst.max.toLocaleString()}</p>
                <p className="text-white/50 text-xs mt-1">incl. GST · ~{distKm} km road distance · final price confirmed on arrival</p>
              </div>
            )}

            {/* Photos */}
            <PhotoUploader photos={photos} onChange={setPhotos} label="Photos of Your Vehicle (optional)" />
          </>)}

          {/* ─── CASH FOR CARS FLOW ─────────────────────────────────── */}
          {svc.type === 'cash' && !showCashResult && (<>
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <p className="text-green-800 font-black text-sm">💵 Cash for Cars — Same Day Payment</p>
              <p className="text-green-700 text-xs mt-0.5">Fill in your vehicle details for an instant estimate. We'll call with your final offer.</p>
            </div>

            {/* Vehicle type */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Vehicle Type</p>
              <div className="grid grid-cols-3 gap-2">
                {VEHICLE_TYPES.map(v => (
                  <button key={v.id} onClick={() => set('vehicle_type')(v.id)}
                    className={`p-3 rounded-xl border-2 text-xs font-semibold text-center transition-all ${form.vehicle_type===v.id ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 text-gray-700'}`}>
                    <div className="text-xl mb-1">{v.icon}</div>{v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Vehicle details */}
            <div className="grid grid-cols-3 gap-3">
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Year</span>
                <input value={form.vehicle_year} onChange={set('vehicle_year')} className={`${inputCls} mt-1`} placeholder="2012" /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Make</span>
                <input value={form.vehicle_make} onChange={set('vehicle_make')} className={`${inputCls} mt-1`} placeholder="Toyota" /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Model</span>
                <input value={form.vehicle_model} onChange={set('vehicle_model')} className={`${inputCls} mt-1`} placeholder="Corolla" /></label>
            </div>
            <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Rego (optional)</span>
              <input value={form.vehicle_rego} onChange={set('vehicle_rego')} className={`${inputCls} mt-1`} placeholder="123 ABC" /></label>

            {/* Condition */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Vehicle Condition</p>
              <div className="grid grid-cols-5 gap-1.5">
                {CAR_CONDITIONS.map(c => (
                  <button key={c} onClick={() => set('cash_condition')(c)}
                    className={`py-2.5 rounded-xl border-2 text-xs font-bold text-center transition-all ${form.cash_condition===c ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Live estimate — shows as soon as condition selected */}
            {cashEst && (
              <div className="bg-green-600 rounded-2xl p-5 text-center text-white">
                <p className="text-white/70 text-xs font-bold uppercase tracking-wide mb-1">Estimated Cash Offer</p>
                <p className="text-4xl font-black">${cashEst.min.toLocaleString()} – ${cashEst.max.toLocaleString()}</p>
                <p className="text-white/60 text-xs mt-1">Estimate only · final offer after we inspect the vehicle</p>
              </div>
            )}

            {/* Expectation picker */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Your Price Expectation</p>
              <p className="text-xs text-gray-400 mb-2">This helps us prepare — select the range you're hoping for</p>
              <div className="grid grid-cols-2 gap-2">
                {EXPECTATIONS.map(e => (
                  <button key={e.id} onClick={() => set('expectation')(e.id)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${form.expectation===e.id ? 'border-apex-blue-500 bg-apex-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <p className={`text-sm font-bold ${form.expectation===e.id ? 'text-apex-blue-700' : 'text-gray-800'}`}>{e.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{e.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Location */}
            <label className="block">
              <span className="text-xs font-bold text-gray-500 uppercase">Vehicle Location *</span>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                <input value={form.pickup} onChange={set('pickup')} className={`${inputCls} pl-9`} placeholder="Your suburb or postcode" />
              </div>
            </label>

            {/* Photos */}
            <PhotoUploader photos={photos} onChange={setPhotos} label="Photos of Your Vehicle (helps us assess value)" />
          </>)}

          {/* Cash for cars result screen */}
          {svc.type === 'cash' && showCashResult && !cashEstLoading && (
            <CashResult
              estimate={cashEst || { min:100, max:300 }}
              expectation={form.expectation}
              form={form}
              photos={photos}
              onBookNow={() => goToBook()}
              onCallUs={() => {}}
            />
          )}

          {/* Cash for Cars loading animation */}
          {svc.type === 'cash' && cashEstLoading && (
            <TowTruckAnimation message="Checking your vehicle value..." />
          )}

          {/* ─── FREE REMOVAL FLOW ─────────────────────────────────── */}
          {svc.type === 'free' && !showCashResult && (<>
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 text-center">
              <p className="text-4xl font-black text-green-600 mb-1">100% FREE</p>
              <p className="text-green-700 text-sm">We come to you — no fees of any kind, anywhere in Brisbane</p>
            </div>
            <label className="block">
              <span className="text-xs font-bold text-gray-500 uppercase">Vehicle Location *</span>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                <input value={form.pickup} onChange={set('pickup')} className={`${inputCls} pl-9`} placeholder="Your suburb or postcode" />
              </div>
            </label>
            <PhotoUploader photos={photos} onChange={setPhotos} label="Vehicle Photos (optional)" />
          </>)}

          {/* ─── Contact details (not shown on cash result screen or during loading) ── */}
          {!showCashResult && !cashEstLoading && (
            <>
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Your Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Name *</span>
                    <input value={form.customer_name} onChange={set('customer_name')} className={`${inputCls} mt-1`} placeholder="John Smith" /></label>
                  <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone *</span>
                    <input type="tel" value={form.customer_phone} onChange={set('customer_phone')} className={`${inputCls} mt-1`} placeholder="07 0000 0000" /></label>
                </div>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Email (optional)</span>
                  <input type="email" value={form.customer_email} onChange={set('customer_email')} className={`${inputCls} mt-1`} placeholder="john@email.com" /></label>
              </div>

              <button onClick={submit}
                disabled={!canSubmit || loading || (svc.type === 'towing' && !estimateReady && !distError)}
                className={`w-full py-4 rounded-xl font-black flex items-center justify-center gap-2 text-base transition-all
                  ${canSubmit && (svc.type !== 'towing' || estimateReady || distError) && !loading
                    ? svc.type==='cash'   ? 'bg-green-600 text-white hover:bg-green-700'
                    : svc.type==='free'   ? 'bg-green-500 text-white hover:bg-green-600'
                                         : 'bg-apex-yellow-500 text-apex-blue-700 hover:bg-apex-yellow-400'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                {loading ? 'Submitting...' :
                  svc.type === 'towing' && !estimateReady && !distError && form.pickup && form.dropoff ? 'Calculating...' :
                  svc.type === 'towing' && !form.pickup ? 'Enter both suburbs first' :
                  svc.type==='cash' ? <><DollarSign className="w-5 h-5"/>Get My Cash Offer</> :
                  svc.type==='free' ? <><Calculator className="w-5 h-5"/>Request Free Removal</> :
                                     <><Calculator className="w-5 h-5"/>Get My Quote</>}
              </button>

              <p className="text-center text-xs text-gray-400">
                Or call us: <a href={telHref()} className="font-bold text-apex-blue-700 underline">{settings.company_phone}</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}